import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Clock, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: "Quality Craftsmanship",
      description: "Every tile is laid with precision and care, ensuring a flawless finish that lasts."
    },
    {
      icon: Users,
      title: "Customer Focus",
      description: "We work closely with you to understand your vision and bring it to life."
    },
    {
      icon: Clock,
      title: "Reliability",
      description: "We turn up when we say we will and complete projects on time."
    },
    {
      icon: Shield,
      title: "Fully Insured",
      description: "Complete peace of mind with full public liability insurance."
    }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50 grid-pattern">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-montserrat">
                About DC Tiling
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                With over 15 years of experience, DC Tiling has become a trusted name for quality tiling installations across Greater Manchester and surrounding areas.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Based in Greater Manchester, we serve homeowners and businesses throughout the region, delivering exceptional craftsmanship and outstanding customer service on every project.
              </p>
              <Link to="/quote">
                <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="about-hero-cta">
                  Get Your Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/11806490/pexels-photo-11806490.jpeg?w=800"
                  alt="DC Tiling professional at work"
                  className="w-full h-full object-cover"
                  data-testid="about-hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-montserrat">
              Our Story
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              DC Tiling was founded with a simple mission: to provide homeowners with high-quality tiling that transforms their spaces and stands the test of time.
            </p>
            <p className="text-lg text-gray-600 mb-6">
              What started as a one-man operation has grown into a respected local business, thanks to our commitment to quality, reliability, and customer satisfaction.
            </p>
            <p className="text-lg text-gray-600">
              Today, we're proud to have completed hundreds of projects across Greater Manchester, from simple splashbacks to complete bathroom renovations. Every project receives the same attention to detail and dedication to excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center font-montserrat">
            Why Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2 font-montserrat">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-montserrat">
                Areas We Serve
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Serving Greater Manchester and surrounding areas including:
              </p>
              <ul className="grid grid-cols-2 gap-3 mb-8">
                {["Manchester", "Salford", "Stockport", "Trafford", "Bolton", "Bury", "Oldham", "Rochdale", "Tameside", "Wigan"].map((area) => (
                  <li key={area} className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 bg-primary rounded-full" />
                    {area}
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5">
                  Contact Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d152515.87654752788!2d-2.3795674!3d53.4723272!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487a4d4c5226f5db%3A0xd9be143804fe6baa!2sGreater%20Manchester!5e0!3m2!1sen!2suk!4v1700000000000!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Area Map - Greater Manchester"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Ready to Transform Your Space?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Get in touch today for a free, no-obligation quote.
          </p>
          <Link to="/quote">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="about-final-cta">
              Start My Tile Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
